import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ConfirmDialog,
  EmptyState,
  FormField,
  Input,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components';
import { useApi, useApiProblem, toApiError } from '@/shared/api';
import { GuestEmailRow } from './GuestEmailRow';
import { onboardingErrorText } from './onboardingErrors';
import { PendingOnboardingsPanel } from './PendingOnboardingsPanel';

const pageSize = 25;
/** Der Server filtert nur mit der ganzen Kennung; ein Anfang wäre ein 400 und sähe aus wie ein Ausfall. */
const BOOKING_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function GuestEmailsSection() {
  const api = useApi();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [bookingFilter, setBookingFilter] = useState('');
  const partial = draft.trim() !== '' && !BOOKING_ID.test(draft.trim());
  useEffect(() => {
    const next = draft.trim();
    if (next !== '' && !BOOKING_ID.test(next)) return;
    const id = window.setTimeout(() => {
      setBookingFilter(next);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(id);
  }, [draft]);
  const [page, setPage] = useState(1);
  const [reIssuingId, setReIssuingId] = useState<string | null>(null);
  // Eine Mail geht sofort an den Gast: vorher fragt ein Dialog, an welche Buchung.
  const [confirming, setConfirming] = useState<{ id: string; bookingId: string } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['system', 'onboardingLinks', bookingFilter, page],
    queryFn: () => api.system.onboardingLinks(bookingFilter || undefined, page, pageSize),
    placeholderData: (previous) => previous,
  });

  const problem = useApiProblem(error);
  const reIssue = useMutation({
    mutationFn: (bookingId: string) => api.system.onboardingLinkReIssue(bookingId),
    onSuccess: () => {
      toast.success('Die Gäste-E-Mail liegt im Ausgang.');
      void qc.invalidateQueries({ queryKey: ['system', 'onboardingLinks'] });
      void qc.invalidateQueries({ queryKey: ['system', 'pendingOnboardings'] });
    },
    onError: async (err) => {
      toast.error(
        onboardingErrorText(await toApiError(err), 'Die E-Mail konnte nicht gesendet werden.'),
      );
    },
    onSettled: () => setReIssuingId(null),
  });

  const items = data?.items ?? [];

  return (
    <>
      <PendingOnboardingsPanel />
      <div className="mb-4 sm:max-w-sm">
        <FormField
          label="Nach Buchung filtern"
          hint={
            partial
              ? 'Bitte die vollständige Buchungs-Kennung eingeben (36 Zeichen) – nach einem Anfang filtert der Server nicht.'
              : 'Vollständige Buchungs-Kennung, zu finden in der Adresse der Buchung.'
          }
        >
          <Input type="search" value={draft} onChange={(event) => setDraft(event.target.value)} />
        </FormField>
      </div>

      {error ? (
        <EmptyState
          icon="Error"
          title="Die Zugänge konnten nicht geladen werden"
          description={
            problem
              ? problem.status === 0
                ? 'Keine Verbindung.'
                : `Code ${problem.title}`
              : undefined
          }
          action={{ label: 'Erneut laden', onClick: () => void refetch() }}
        />
      ) : !isLoading && items.length === 0 ? (
        <EmptyState
          icon="Mail"
          title="Keine Gäste-E-Mails"
          description="Sobald eine Buchung ihren Gastzugang bekommt, steht sie hier."
        />
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zustand</TableHead>
                <TableHead>Buchung</TableHead>
                <TableHead>Gültig bis</TableHead>
                <TableHead>Versand</TableHead>
                <TableHead className="text-right">Aufrufe</TableHead>
                <TableHead>
                  <span className="sr-only">Aktion</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((link) => (
                <GuestEmailRow
                  key={link.id}
                  link={link}
                  retrying={reIssuingId === link.id}
                  onReIssue={() => setConfirming({ id: link.id, bookingId: link.bookingId })}
                />
              ))}
            </TableBody>
          </Table>
          <ConfirmDialog
            open={confirming !== null}
            onOpenChange={(open) => !open && setConfirming(null)}
            title="E-Mail erneut senden?"
            description={`Der Zugang zur Buchung ${confirming?.bookingId.slice(0, 8) ?? ''}… geht noch einmal an den Gast – per E-Mail oder, bei Kanalbuchungen, als Nachricht im Buchungsportal – mit einem frisch ausgestellten Link. Bisherige Links aus Mail und Chat gelten weiter.`}
            confirmLabel="Erneut senden"
            variant="brand"
            onConfirm={() => {
              if (!confirming) return;
              setReIssuingId(confirming.id);
              reIssue.mutate(confirming.bookingId);
              setConfirming(null);
            }}
          />
          {data && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={Number(data.total)}
              onPageChange={setPage}
            />
          )}
        </>
      )}
    </>
  );
}
